/**
 * Bridges UI actions to one replaceable VoiceChatRTC instance, then projects
 * provider and browser events into Redux, persistence APIs, and reconciliation.
 * Redux remains serializable UI state; this module retains the nonserializable
 * WebRTC transport, streams, timers, and mounted audio resource. Components
 * dispatch actions and read selectors, never reach into the live RTC.
 */

import { SimpleHttpError } from "@/types/http"
import type { LocationSearchHit } from "@/types/place"
import type {
  TurnDetectionRequest,
  VoiceNoiseReduction,
  VoiceSessionTraceFlushReason,
} from "@/types/voice-chat"
import {
  VOICE_AUDIO_SYSTEM_DEVICE_ID,
  type StopVoiceSessionArgs,
  type VoiceChatRTCDeps,
} from "@/types/voice-chat-rtc"
import { isAnyOf } from "@reduxjs/toolkit"
import { v4 as uuidv4 } from "uuid"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { nowMillis } from "@/lib/date-utils"
import { errorMessage } from "@/lib/exception-utils"
import { ExploreApi } from "@/lib/explore-api"
import {
  isExpectedMicAccessError,
  micAccessErrorMessage,
} from "@/lib/mic-errors"
import { wait } from "@/lib/promise-utils"
import { reportUnexpectedError } from "@/lib/report-unexpected-error"
import {
  reportTranscriptAnnotationFailure,
  voiceChatApi,
} from "@/lib/storage/api/voice-chat-api"
import { markCompletedVoiceChat } from "@/lib/storage/chat-tab-outside-nav"
import { fetchNewMessages } from "@/lib/storage/chat-thunks"
import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import { geoStartRequested } from "@/lib/storage/geolocation-actions"
import { positionUpdated } from "@/lib/storage/geolocation-slice"
import { setPreviousPage } from "@/lib/storage/navigation-slice"
import type { AppDispatch, RootState } from "@/lib/storage/redux-store"
import userSlice, { selectChatId } from "@/lib/storage/user-slice"
import {
  selectVoiceChatState,
  voiceAssistantStopRequested,
  voiceChatActions,
  voiceModeOpenFailed,
  voiceModeRequested,
  voiceSessionStartRequested,
  voiceSessionStopRequested,
  type VoiceChatState,
  type VoiceModeCloseReason,
  type VoiceSessionFailure,
} from "@/lib/storage/voice-chat-slice"
import {
  playEndCallSound,
  playStartCallSound,
  primeCallSounds,
} from "@/lib/voice-chat/call-sounds"
import {
  acquireMicrophone,
  VoiceChatRTC,
  VoiceMicrophoneAcquisitionError,
  VoiceSessionConnectionError,
} from "@/lib/voice-chat/voice-chat-rtc"
import { voiceMicConstraints } from "@/lib/voice-chat/voice-chat-utils"
import {
  buildLightVoiceTrace,
  getActiveVoiceTraceContext,
  recordVoiceTraceForContext,
  startVoiceTraceContext,
  stopVoiceTraceContext,
  updateVoiceTraceContext,
  voiceTracer,
} from "@/lib/voice-trace/voice-trace"

export const voiceChatMiddleware = createMtListenerMiddleware(
  "voice-chat-middleware"
)
const startAppListening = voiceChatMiddleware.startListening.withTypes<
  RootState,
  AppDispatch
>()
const exploreApi = new ExploreApi(buildClientAxios())
const pipelineTrace = voiceTracer("pipeline")
const settingsTrace = voiceTracer("settings")
const transportTrace = voiceTracer("transport")

// Total connect attempts per start request: the initial one plus one silent
// retry, kept well under the backend's 10/min voice_chat_create_session limit
// even across rapid re-taps.
const VOICE_SESSION_START_ATTEMPTS = 2

// A rebuild mirrors the start path's retry rule: one immediate attempt (the
// media path died while HTTPS kept working, so it is reachable now) and one
// retry for a transient negotiate failure. Each attempt is also bounded in
// time — a negotiate can run ~87s inside the backend's own retries, and one
// that succeeds while the media path is still down waits on the data channel
// forever. Two bounded attempts put the worst case near 32s.
const VOICE_RECONNECT_ATTEMPTS = 2
const VOICE_RECONNECT_RETRY_MS = 2_000
const VOICE_RECONNECT_ATTEMPT_TIMEOUT_MS = 15_000

// The active RTC is replaceable session ownership: a successor must not receive
// callbacks from its predecessor. The audio element is a mounted UI resource
// reused across sessions, so it outlives whichever RTC currently owns playback.
let activeVoiceRtc: VoiceChatRTC | null = null
let locationEntityRef: string | null = null
let locationRequestSeq = 0
let voiceAudioElement: HTMLAudioElement | null = null

// Bumped by every deliberate session transition (start, stop, module reset) so
// a reconnect ladder can tell it has been superseded while no `activeVoiceRtc`
// exists to authenticate against.
let voiceSessionEpoch = 0

// The two-event funnel, spanning the button click (voiceModeRequested) →
// voiceModeClosed: every attempt emits exactly one VoiceSessionStart and one
// VoiceSessionEnd, so an outcome is always a property filter on End rather
// than a subtraction between events. The attempt is middleware-local rather
// than Redux state — nothing renders from it — and closure-scoped so these
// five operations are the only things that can write it. One at a time,
// matching the single live session the RTC allows.
type VoiceSessionEndReason = VoiceModeCloseReason | "failed_to_start"

const createVoiceSessionAnalytics = () => {
  type VoiceSessionAttempt = {
    voiceSessionId: string
    // Both null until the overlay opens: an attempt is born at the click,
    // before the mic grant and the chat it will belong to.
    chatId: number | null
    openedAtMs: number | null
    // Where the open flow died, or what ended a live session early; null when
    // neither happened.
    failure: VoiceSessionFailure | null
    failureExpected: boolean | null
    // Tallies for the failures the session survived — the paths the UI now
    // swallows on purpose, kept countable here so swallowing them visually
    // doesn't also swallow them from the funnel.
    startRetryCount: number
    benignErrorCount: number
    benignErrorCodes: Set<string>
    reconnectCount: number
    reconnectGapMs: number
    // Cut replies and the assistant speech they were cut out of: the barge-in
    // count only reads as a rate against the audio it interrupted.
    phantomBargeInCount: number
    shortBargeInCount: number
    assistantAudioMs: number
    // What the provider session actually negotiated, from the session
    // response; null until a session connects.
    turnDetectionType: TurnDetectionRequest["type"] | null
    noiseReduction: VoiceNoiseReduction | null
  }
  let attempt: VoiceSessionAttempt | null = null

  const begin = () => {
    attempt = {
      voiceSessionId: uuidv4(),
      chatId: null,
      openedAtMs: null,
      failure: null,
      failureExpected: null,
      startRetryCount: 0,
      benignErrorCount: 0,
      benignErrorCodes: new Set(),
      reconnectCount: 0,
      reconnectGapMs: 0,
      phantomBargeInCount: 0,
      shortBargeInCount: 0,
      assistantAudioMs: 0,
      turnDetectionType: null,
      noiseReduction: null,
    }
    sendAnalyticsEvent({
      name: "VoiceSessionStart",
      properties: { voice_session_id: attempt.voiceSessionId },
    })
    return attempt
  }

  return {
    begin,

    // Voice mode opened, so the attempt has a chat and a clock. An open with
    // no click before it (tests) begins its own, so a session is never
    // half-reported.
    opened: (chatId: number) => {
      const opening = attempt ?? begin()
      opening.chatId = chatId
      opening.openedAtMs = nowMillis()
      return opening.voiceSessionId
    },

    // Recorded only when the attempt is the caller's. A start-pipeline failure
    // names the chat it was for, and a superseding attempt must not inherit
    // it; a failure before voice mode opened passes null, which is what an
    // unopened attempt holds.
    failed: (
      chatId: number | null,
      failure: VoiceSessionFailure,
      expected: boolean | null
    ) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.failure = failure
      attempt.failureExpected = expected
    },

    // The silent start retry fired: the first connect attempt failed and
    // nothing surfaced. Counted so a rescued start stays distinguishable from
    // a clean one.
    startRetried: (chatId: number | null) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.startRetryCount += 1
    },

    // A provider `error` event classified benign and swallowed mid-session.
    // The distinct codes ride along so a "benign" code spiking in the field is
    // visible without a trace in hand.
    benignProviderError: (chatId: number | null, code: string | null) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.benignErrorCount += 1
      attempt.benignErrorCodes.add(code ?? "unknown")
    },

    // The settings the session resolved to, so the tallies above can be read
    // by regime without inferring it from the client's app version.
    negotiated: (
      chatId: number | null,
      turnDetectionType: TurnDetectionRequest["type"],
      noiseReduction: VoiceNoiseReduction
    ) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.turnDetectionType = turnDetectionType
      attempt.noiseReduction = noiseReduction
    },

    // A barge-in that cancelled the assistant and transcribed to nothing.
    phantomBargeIn: (chatId: number | null) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.phantomBargeInCount += 1
    },

    shortBargeIn: (chatId: number | null) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.shortBargeInCount += 1
    },

    assistantAudioElapsed: (chatId: number | null, elapsedMs: number) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.assistantAudioMs += elapsedMs
    },

    reconnected: (chatId: number | null, gapMs: number) => {
      if (attempt === null || attempt.chatId !== chatId) {
        return
      }
      attempt.reconnectCount += 1
      attempt.reconnectGapMs += gapMs
    },

    end: (reason: VoiceSessionEndReason) => {
      if (attempt === null) {
        return
      }
      const ended = attempt
      attempt = null
      sendAnalyticsEvent({
        name: "VoiceSessionEnd",
        properties: {
          chat_id: ended.chatId ?? undefined,
          voice_session_id: ended.voiceSessionId,
          reason,
          // Measured from the overlay opening, not the click: the click→open
          // gap is the user deciding on the permission prompt, not session
          // minutes. Left unset for an attempt that never opened, so it can't
          // skew the average.
          duration_ms:
            ended.openedAtMs !== null
              ? nowMillis() - ended.openedAtMs
              : undefined,
          failure: ended.failure ?? undefined,
          failure_expected: ended.failureExpected ?? undefined,
          // Always emitted (0 included) so Amplitude averages read straight
          // off the property instead of treating quiet sessions as missing.
          start_retry_count: ended.startRetryCount,
          reconnect_count: ended.reconnectCount,
          reconnect_gap_ms: ended.reconnectGapMs,
          benign_error_count: ended.benignErrorCount,
          phantom_barge_in_count: ended.phantomBargeInCount,
          short_barge_in_count: ended.shortBargeInCount,
          assistant_audio_ms: ended.assistantAudioMs,
          turn_detection_type: ended.turnDetectionType ?? undefined,
          noise_reduction: ended.noiseReduction ?? undefined,
          benign_error_codes:
            ended.benignErrorCodes.size > 0
              ? [...ended.benignErrorCodes].sort()
              : undefined,
        },
      })
      return ended
    },

    reset: () => {
      attempt = null
    },
  }
}
const voiceSessionAnalytics = createVoiceSessionAnalytics()
let failedStartTraceUploadCount = 0

async function uploadLightVoiceTrace(
  dispatch: AppDispatch,
  chatId: number,
  voiceSessionId: string,
  flushReason: VoiceSessionTraceFlushReason
) {
  try {
    const trace = await buildLightVoiceTrace(chatId, voiceSessionId)
    if (trace.entryCount === 0) {
      return
    }
    await dispatch(
      voiceChatApi.endpoints.createSessionTrace.initiate({
        chatId,
        voiceSessionId,
        flushReason,
        contentEncoding: trace.contentEncoding,
        entryCount: trace.entryCount,
        droppedCount: trace.droppedCount,
        trace: trace.file,
      })
    ).unwrap()
  } catch (error) {
    recordVoiceTraceForContext(
      { chatId, voiceSessionId },
      {
        category: "pipeline",
        level: "error",
        summary: "voice_light_trace_upload_failed",
        detail: { message: errorMessage(error) },
      }
    )
  }
}

export const setVoiceAudioElement = (element: HTMLAudioElement | null) => {
  voiceAudioElement = element
  activeVoiceRtc?.setAudioElement(element)
}

// Mic + assistant audio levels (0..1, or null when unavailable) for the
// voice orb, sampled from the live session's WebRTC stats. A snapshot, not a
// stream: wrapping the streams in a Web Audio AudioContext would disable
// Chrome's echo canceller.
export const sampleVoiceAudioLevels = (): Promise<{
  mic: number | null
  assistant: number | null
}> =>
  activeVoiceRtc?.sampleAudioLevels() ??
  Promise.resolve({ mic: null, assistant: null })

// Resolves the customer's position to a named area. A 404 (open water, remote
// land) or 429 is an expected miss and only traced; anything else is a bug.
async function lookupLocation(position: {
  latitude: number
  longitude: number
}): Promise<LocationSearchHit | null> {
  try {
    return await exploreApi.fetchLocationByLatLong(
      position.latitude,
      position.longitude
    )
  } catch (error) {
    const status =
      error instanceof SimpleHttpError ? error.response?.status : undefined
    if (status === 404 || status === 429) {
      pipelineTrace.warn("location_update_lookup_failed", { status })
    } else {
      reportUnexpectedError(error, "voice-chat-middleware", {
        operation: "location_update",
      })
    }
    return null
  }
}

async function deliverLocationUpdate(
  rtc: VoiceChatRTC,
  getState: () => RootState
) {
  const position = getState().geolocation.position
  if (!position) {
    return
  }
  const seq = ++locationRequestSeq
  const epoch = voiceSessionEpoch
  const hit = await lookupLocation(position)
  if (!hit || seq !== locationRequestSeq) {
    return
  }
  if (epoch !== voiceSessionEpoch || activeVoiceRtc !== rtc) {
    return
  }
  if (hit.entity_ref === locationEntityRef) {
    return
  }
  if (rtc.sendLocationUpdate(hit)) {
    locationEntityRef = hit.entity_ref
  }
}

function buildVoiceRtcDeps(
  chatId: number,
  dispatch: AppDispatch,
  getState: () => RootState,
  isCurrentRtc: () => boolean
): VoiceChatRTCDeps {
  // A user turn can persist successfully more than once (transcript revisions,
  // retry races), so emit the analytics event only for the first success.
  const analyticsEmittedTurnIds = new Set<string>()
  return {
    chatId,
    getVoiceState: () => selectVoiceChatState(getState()),
    onCustomerSpeechStarted: () => {
      if (isCurrentRtc() && activeVoiceRtc) {
        void deliverLocationUpdate(activeVoiceRtc, getState)
      }
    },
    onTurnTransition: (transition) =>
      dispatch(voiceChatActions.turnTransitionApplied(transition)),
    onChatRenamed: ({ chatId: renamedChatId, name }) =>
      dispatch(userSlice.actions.updateChat({ id: renamedChatId, name })),
    onToolStarted: (payload) =>
      dispatch(voiceChatActions.toolCallStarted(payload)),
    onToolProgress: (payload) =>
      dispatch(voiceChatActions.toolCallProgressed(payload)),
    onToolSucceeded: (payload) =>
      dispatch(voiceChatActions.toolCallSucceeded(payload)),
    onToolFailed: (payload) =>
      dispatch(voiceChatActions.toolCallFailed(payload)),
    onPlaceAnnotationCandidates: async (payload) => {
      const { openaiCallId } = selectVoiceChatState(getState())
      if (openaiCallId !== payload.openaiCallId) {
        return
      }

      try {
        const response = await dispatch(
          voiceChatApi.endpoints.createTranscriptAnnotation.initiate({
            chatId,
            openaiCallId: payload.openaiCallId,
            providerEventId: payload.providerEventId,
            messageId: payload.messageId,
            transcript: payload.transcript,
            candidates: payload.candidates,
            toolCallIds: payload.toolCallIds,
          })
        ).unwrap()
        if (
          response.deleted !== true &&
          selectVoiceChatState(getState()).openaiCallId === payload.openaiCallId
        ) {
          dispatch(
            voiceChatActions.turnAnnotationApplied({
              providerEventId: payload.providerEventId,
              markers: response.markers,
              places: response.places,
            })
          )
        }
      } catch (error) {
        pipelineTrace.warn("voice_place_annotation_failed", {
          providerEventId: payload.providerEventId,
          reason: "backend_resolution_failed",
          message: errorMessage(error, "Could not annotate voice transcript."),
        })
      }
    },
    reportPlaceAnnotationFailure: reportTranscriptAnnotationFailure,
    onTurnPersisting: (payload) =>
      dispatch(voiceChatActions.turnPersisting(payload)),
    onTurnPersisted: (payload) =>
      dispatch(voiceChatActions.turnPersisted(payload)),
    onTurnPersistFailed: (payload) =>
      dispatch(voiceChatActions.turnPersistFailed(payload)),
    onCaptionRateMeasured: (rate) =>
      dispatch(voiceChatActions.captionRateMeasured(rate)),
    onAssistantSpeakingChanged: (payload) =>
      dispatch(voiceChatActions.assistantSpeakingChanged(payload)),
    onTransportReconnectingChanged: (reconnecting) => {
      if (!isCurrentRtc()) {
        return
      }
      dispatch(
        reconnecting
          ? voiceChatActions.transportReconnecting()
          : voiceChatActions.transportRecovered()
      )
    },
    onBenignProviderError: (code) => {
      if (!isCurrentRtc()) {
        return
      }
      voiceSessionAnalytics.benignProviderError(chatId, code)
    },
    onPhantomBargeIn: () => {
      if (!isCurrentRtc()) {
        return
      }
      voiceSessionAnalytics.phantomBargeIn(chatId)
    },
    onShortBargeIn: () => {
      if (!isCurrentRtc()) {
        return
      }
      voiceSessionAnalytics.shortBargeIn(chatId)
    },
    onAssistantAudioElapsed: (elapsedMs) => {
      if (!isCurrentRtc()) {
        return
      }
      voiceSessionAnalytics.assistantAudioElapsed(chatId, elapsedMs)
    },
    onConnectionLost: (error) => {
      if (!isCurrentRtc()) {
        return
      }
      transportTrace.warn("voice_session_connect_failed", {
        reason:
          error instanceof VoiceSessionConnectionError
            ? error.reason
            : error.message,
      })
      // A dead transport is rebuilt, not reported. Every reason reaching here
      // is unrecoverable on this call, so the ladder starts immediately.
      void reconnectVoiceSession(dispatch, getState, chatId)
    },
    persistTranscript: async (args) => {
      const result = await dispatch(
        voiceChatApi.endpoints.createTranscriptMessage.initiate(args)
      ).unwrap()
      if (
        args.role === "user" &&
        result.deleted !== true &&
        !analyticsEmittedTurnIds.has(args.providerEventId)
      ) {
        analyticsEmittedTurnIds.add(args.providerEventId)
        sendAnalyticsEvent({
          name: "ChatUserMessageAdd",
          properties: {
            prompt: result.body,
            chatType: "individual",
            chat: { id: args.chatId },
            voice_chat: true,
          },
        })
      }
      return result
    },
    runTool: (args) =>
      dispatch(voiceChatApi.endpoints.runTool.initiate(args)).unwrap(),
    runGraphqlTools: (args) =>
      dispatch(voiceChatApi.endpoints.runGraphqlTools.initiate(args)).unwrap(),
    fetchToolResult: (args) =>
      dispatch(
        voiceChatApi.endpoints.getToolResult.initiate(args, {
          forceRefetch: true,
        })
      ).unwrap(),
    fetchToolMedia: (args) =>
      dispatch(
        voiceChatApi.endpoints.getToolMedia.initiate(args, {
          forceRefetch: true,
        })
      ).unwrap(),
    refreshToolCalls: (id) =>
      dispatch(
        voiceChatApi.endpoints.listToolCalls.initiate(id, {
          forceRefetch: true,
        })
      ).unwrap(),
  }
}

// Pull just-persisted voice turns into the active main-chat thread.
// fetchNewMessages is incremental and self-guards against overlapping loads,
// so this is cheap to call per turn.
function syncVoiceTurnsIntoMainChat(
  dispatch: AppDispatch,
  getState: () => RootState,
  voiceChatId: number | null | undefined
) {
  if (voiceChatId && selectChatId(getState()) === voiceChatId) {
    void dispatch(fetchNewMessages())
  }
}

// Becomes the active session; callbacks authenticate by instance.
function installVoiceRtc(
  chatId: number,
  dispatch: AppDispatch,
  getState: () => RootState
) {
  const rtc: VoiceChatRTC = new VoiceChatRTC(
    buildVoiceRtcDeps(chatId, dispatch, getState, () => activeVoiceRtc === rtc)
  )
  rtc.setAudioElement(voiceAudioElement)
  activeVoiceRtc = rtc
  locationEntityRef = null
  locationRequestSeq = 0
  rtc.subscribeVoiceChat()
  return rtc
}

// Output speed rides the negotiate, so a reconnect reads the current setting
// rather than the one the session started with. Turn detection and noise
// reduction are not sent at all: the server resolves them from the audio profile.
const negotiateVoiceSession =
  (
    dispatch: AppDispatch,
    chatId: number,
    settings: VoiceChatState,
    signal?: AbortSignal
  ) =>
  async (sdpOffer: string) => {
    const session = await dispatch(
      voiceChatApi.endpoints.createOpenaiSession.initiate({
        chatId,
        sdpOffer,
        signal,
        speed: settings.selectedSpeed,
        entityRef: settings.voiceModeEntityRef,
      })
    ).unwrap()
    voiceSessionAnalytics.negotiated(
      chatId,
      session.turnDetectionType,
      session.noiseReduction
    )
    // Recorded here rather than at connect: this is where the settings the
    // provider session actually runs on arrive, for a rebuilt session as well
    // as the first one.
    pipelineTrace.info("voice_session_turn_authority", {
      turnDetectionType: session.turnDetectionType,
      noiseReduction: session.noiseReduction,
      turnDetectionCreateResponse: true,
      turnDetectionInterruptResponse: true,
      responseAuthority: "provider_user_turns_client_tool_results",
      selectedSpeed: settings.selectedSpeed,
      reasoningEffort: "low",
      openaiCallId: session.openaiCallId,
    })
    return session
  }

// Rebuild a session whose transport died under a live conversation. It is the
// only recovery available — the Realtime calls API takes one SDP exchange per
// call, so there is no re-offer or ICE restart — and it avoids the stop path,
// whose `disconnected()` would clear the captions the customer is reading.
async function reconnectVoiceSession(
  dispatch: AppDispatch,
  getState: () => RootState,
  chatId: number
) {
  const epoch = ++voiceSessionEpoch
  const owned = () => voiceSessionEpoch === epoch
  const startedAtMs = nowMillis()
  pipelineTrace.info("voice_session_reconnect_started", { chatId })
  // Mid-call "Connecting…" rather than the cold-start treatment.
  dispatch(voiceChatActions.transportReconnecting())

  // Teardown flushes the dead session's unpersisted turns; history replay is
  // built from the persisted transcript, so they have to land before the
  // negotiate reads it. Tool reconciliation is skipped: `sessionReconciled`
  // would clear the live stream this path exists to preserve.
  const dying = activeVoiceRtc
  activeVoiceRtc = null
  if (dying) {
    const { stoppedChatId, pendingPersists } = dying.teardown()
    // Bounded: the queue retries for over a minute when HTTPS is down too, and
    // that is for the attempts below to discover. The flush carries on behind.
    await Promise.race([
      pendingPersists,
      wait(VOICE_RECONNECT_ATTEMPT_TIMEOUT_MS),
    ])
    syncVoiceTurnsIntoMainChat(dispatch, getState, stoppedChatId)
    if (!owned()) return
  }

  for (let attempt = 1; attempt <= VOICE_RECONNECT_ATTEMPTS; attempt += 1) {
    if (attempt > 1) {
      await wait(VOICE_RECONNECT_RETRY_MS)
    }
    if (!owned()) return
    pipelineTrace.info("voice_session_reconnect_attempt", { attempt })

    const settings = selectVoiceChatState(getState())
    // The profile is cleared when voice mode closes, so a null one means the
    // session this ladder exists to rebuild is already gone.
    if (settings.audioProfile === null) return
    const rtc = installVoiceRtc(chatId, dispatch, getState)
    const deadline = new AbortController()
    const deadlineTimer = setTimeout(
      () => deadline.abort(),
      VOICE_RECONNECT_ATTEMPT_TIMEOUT_MS
    )
    try {
      // A fresh mic grant (teardown stopped the old tracks; no prompt, the
      // permission is held). The deadline also aborts the negotiate request.
      const session = await Promise.race([
        rtc.connect({
          mediaStream: acquireMicrophone(
            voiceMicConstraints(
              settings.audioProfile.microphone,
              settings.selectedInputDeviceId
            )
          ),
          negotiate: negotiateVoiceSession(
            dispatch,
            chatId,
            settings,
            deadline.signal
          ),
        }),
        rejectWhenAborted(deadline.signal),
      ])
      if (!owned()) {
        rtc.close()
        return
      }
      const gapMs = nowMillis() - startedAtMs
      dispatch(
        voiceChatActions.reconnected({
          chatId,
          openaiCallId: session.openaiCallId,
        })
      )
      // The reducer decides which mute survives a rebuild.
      rtc.setMuted(selectVoiceChatState(getState()).muted)
      void rtc.replayHistory()
      voiceSessionAnalytics.reconnected(chatId, gapMs)
      pipelineTrace.info("voice_session_reconnected", {
        attempt,
        gapMs,
        openaiCallId: session.openaiCallId,
      })
      return
    } catch (error) {
      if (!owned()) {
        rtc.close()
        return
      }
      rtc.close()
      activeVoiceRtc = null
      pipelineTrace.warn("voice_session_reconnect_failed", {
        attempt,
        reason:
          error instanceof VoiceSessionConnectionError
            ? error.reason
            : errorMessage(error, "Could not rebuild the voice session."),
      })
    } finally {
      clearTimeout(deadlineTimer)
    }
  }

  pipelineTrace.warn("voice_session_reconnect_exhausted", {
    attempts: VOICE_RECONNECT_ATTEMPTS,
    gapMs: nowMillis() - startedAtMs,
  })
  performStop(dispatch, getState, {})
  // Fixed copy only — the transport internals are in the traces above.
  dispatch(voiceChatActions.failed("Connection lost. Please try again."))
  // Otherwise the eventual VoiceSessionEnd reads user_ended.
  voiceSessionAnalytics.failed(chatId, "connection", null)
}

// connect() itself may never settle; this is what the deadline races against.
function rejectWhenAborted(signal: AbortSignal): Promise<never> {
  return new Promise((_, reject) => {
    signal.addEventListener(
      "abort",
      () =>
        reject(new VoiceSessionConnectionError("reconnect_attempt_timeout")),
      { once: true }
    )
  })
}

// Drops active RTC ownership and visible connection state synchronously. The old
// persistence queue settles asynchronously; only then may stopped-session tool
// reconciliation and main-chat sync run, each scoped to the captured chat id.
function performStop(
  dispatch: AppDispatch,
  getState: () => RootState,
  { reconcileStoppedSession = true, holdCaptureUntil }: StopVoiceSessionArgs
) {
  // Abandons any reconnect ladder still climbing.
  voiceSessionEpoch += 1
  const rtc = activeVoiceRtc
  if (!rtc) {
    dispatch(voiceChatActions.disconnected())
    return
  }

  const { stoppedChatId, shouldReconcileStoppedSession, pendingPersists } =
    rtc.teardown(holdCaptureUntil)
  activeVoiceRtc = null
  dispatch(voiceChatActions.disconnected())

  void finishStop(dispatch, getState, {
    reconcileStoppedSession,
    stoppedChatId,
    shouldReconcileStoppedSession,
    pendingPersists,
  })
}

async function finishStop(
  dispatch: AppDispatch,
  getState: () => RootState,
  stoppedSession: {
    reconcileStoppedSession: boolean
    stoppedChatId: number | null
    shouldReconcileStoppedSession: boolean
    pendingPersists: Promise<void>
  }
) {
  await stoppedSession.pendingPersists
  const { stoppedChatId } = stoppedSession
  const reconcile =
    stoppedSession.reconcileStoppedSession &&
    stoppedSession.shouldReconcileStoppedSession &&
    stoppedChatId !== null
      ? stoppedChatId
      : null
  if (reconcile !== null) {
    await dispatch(
      voiceChatApi.endpoints.listToolCalls.initiate(reconcile, {
        forceRefetch: true,
      })
    ).unwrap()
    if (selectVoiceChatState(getState()).selectedChatId === reconcile) {
      dispatch(voiceChatActions.sessionReconciled())
    }
  }

  // The per-turn sync pulls turns in live; sync once more now that the
  // persist queue has drained so a turn in flight at stop still lands.
  syncVoiceTurnsIntoMainChat(dispatch, getState, stoppedChatId)
}

// Session start
startAppListening({
  actionCreator: voiceSessionStartRequested,
  effect: async (
    { payload: { chatId, mediaStream, audioProfile } },
    { dispatch, getState }
  ) => {
    pipelineTrace.info("voice_session_start_requested", { chatId })
    voiceSessionEpoch += 1
    activeVoiceRtc?.close()
    dispatch(voiceChatActions.connectingStarted())
    // The mic grant just happened, so the browser's location prompt reads as
    // part of the same action. Every transcript request after the first fix
    // carries User-Coordinates; connection does not wait for it.
    dispatch(geoStartRequested({ reason: "user" }))

    const startState = selectVoiceChatState(getState())
    let attemptMediaStream = mediaStream
    for (let attempt = 1; ; attempt++) {
      const rtc = installVoiceRtc(chatId, dispatch, getState)

      try {
        const session = await rtc.connect({
          mediaStream: attemptMediaStream,
          negotiate: negotiateVoiceSession(dispatch, chatId, startState),
        })

        // A newer session may have replaced this one while it was connecting
        // (StrictMode re-mount, rapid close/re-open, chat switch). Close the
        // stale instance and do NOT dispatch `connected`, or two live sessions
        // would answer at once.
        if (activeVoiceRtc !== rtc) {
          rtc.close()
          return
        }

        dispatch(
          voiceChatActions.connected({
            chatId,
            openaiCallId: session.openaiCallId,
          })
        )

        // The start sound belongs HERE, at readiness, not at the click: it
        // says the microphone is yours, and a cue to speak that lands before
        // the session exists to hear it costs the customer their opening
        // sentence. The mic stays closed for the clip so its own echo cannot
        // be taken for the customer's first words.
        rtc.setStartSoundPlaying(true)
        void playStartCallSound().finally(() => rtc.setStartSoundPlaying(false))
        // Fire-and-forget: the engine waits for its own data channel to open,
        // so this is safe the moment we're connected without racing transport
        // readiness.
        void rtc.replayHistory()
        return
      } catch (error) {
        // Stale connect that lost the race (aborted by teardown, or superseded):
        // just drop this instance. Reporting a failure or calling performStop
        // here would tear down / error the session that replaced it.
        if (activeVoiceRtc !== rtc) {
          rtc.close()
          return
        }
        const microphoneError =
          error instanceof VoiceMicrophoneAcquisitionError ? error : null
        const originalError = microphoneError?.originalError ?? error
        const expectedMicFailure =
          microphoneError !== null && isExpectedMicAccessError(originalError)
        const traceMessage = errorMessage(
          originalError,
          "Could not start voice session."
        )

        // One silent retry for a start-time negotiate/transport failure — a
        // transient 5xx or a network blip should not surface at all. The
        // "Getting ready…" treatment from connectingStarted holds through it.
        // Mic failures are excluded: they are actionable and a re-request
        // would re-prompt. The failed attempt's tracks are stopped by close(),
        // so the retry re-acquires the mic (permission is already granted —
        // no second prompt).
        if (
          attempt < VOICE_SESSION_START_ATTEMPTS &&
          microphoneError === null
        ) {
          pipelineTrace.warn("voice_session_start_retry", {
            attempt,
            message: traceMessage,
          })
          voiceSessionAnalytics.startRetried(chatId)
          rtc.close()
          attemptMediaStream = acquireMicrophone(
            voiceMicConstraints(
              audioProfile.microphone,
              startState.selectedInputDeviceId
            )
          )
          continue
        }

        pipelineTrace.warn("voice_session_start_failed", {
          message: traceMessage,
        })
        // Nothing may await between the ownership check above and these two
        // calls: the check only holds as far as the next suspension, and a
        // session that replaced us in that gap would be the one torn down here.
        performStop(dispatch, getState, {})
        // Mic copy is actionable and stays; every other failure gets fixed
        // copy — the throw's own text is Axios/provider internals.
        const microphoneErrorMessage =
          microphoneError !== null ? micAccessErrorMessage(originalError) : null
        dispatch(
          voiceChatActions.failed(
            microphoneErrorMessage ??
              "Voice chat couldn't start. Please try again."
          )
        )
        if (!expectedMicFailure) {
          reportUnexpectedError(originalError, "voice-chat-middleware", {
            operation:
              microphoneError !== null
                ? "microphone_acquisition"
                : "connection",
          })
        }
        voiceSessionAnalytics.failed(
          chatId,
          microphoneError !== null ? "mic" : "connection",
          expectedMicFailure
        )
        return
      }
    }
  },
})

startAppListening({
  matcher: isAnyOf(voiceChatActions.connected, voiceChatActions.reconnected),
  effect: (_action, { getState }) => {
    if (activeVoiceRtc) {
      void deliverLocationUpdate(activeVoiceRtc, getState)
    }
  },
})

startAppListening({
  predicate: (action, currentState, previousState) =>
    positionUpdated.match(action) &&
    previousState.geolocation.position === null &&
    currentState.geolocation.position !== null,
  effect: (_action, { getState }) => {
    const { connectionStatus } = selectVoiceChatState(getState())
    if (
      connectionStatus !== "listening" &&
      connectionStatus !== "assistant_speaking"
    ) {
      return
    }
    if (activeVoiceRtc) {
      void deliverLocationUpdate(activeVoiceRtc, getState)
    }
  },
})

// Session stop
startAppListening({
  actionCreator: voiceSessionStopRequested,
  effect: ({ payload }, { dispatch, getState }) => {
    performStop(dispatch, getState, payload)
  },
})

// The analytics session brackets the whole open flow, starting at the click:
// an attempt that dies at the permission prompt or chat creation never reaches
// voiceModeOpened, and those attempts are exactly the adoption denominator.
startAppListening({
  actionCreator: voiceModeRequested,
  effect: ({ payload: { chatId } }) => {
    const attempt = voiceSessionAnalytics.begin()
    startVoiceTraceContext({
      chatId,
      voiceSessionId: attempt.voiceSessionId,
    })
  },
})
startAppListening({
  actionCreator: voiceModeOpenFailed,
  effect: ({ payload: { failure, expected } }, { dispatch }) => {
    voiceSessionAnalytics.failed(null, failure, expected ?? null)
    voiceSessionAnalytics.end("failed_to_start")
    const traceContext = getActiveVoiceTraceContext()
    if (traceContext?.chatId != null && failedStartTraceUploadCount < 3) {
      // Cap repeated denied-microphone taps so one page load cannot upload indefinitely.
      failedStartTraceUploadCount += 1
      void uploadLightVoiceTrace(
        dispatch,
        traceContext.chatId,
        traceContext.voiceSessionId,
        "failed_to_start"
      )
    }
    stopVoiceTraceContext()
  },
})

// Chat voice-mode intent → session control. Keyed to the open/close ACTIONS
// (dispatched once, on the user's click) rather than a mount effect, so
// StrictMode's double-invoked effects or any overlay re-mount can never start
// two sessions.
startAppListening({
  actionCreator: voiceChatActions.voiceModeOpened,
  effect: (
    { payload: { chatId, mediaStream, audioProfile } },
    { dispatch }
  ) => {
    voiceSessionAnalytics.opened(chatId)
    updateVoiceTraceContext({ chatId })
    const navigatorWithUserAgentData: Navigator & {
      userAgentData?: {
        brands: ReadonlyArray<{ brand: string; version: string }>
        mobile: boolean
        platform: string
      }
    } = navigator
    const userAgentData = navigatorWithUserAgentData.userAgentData
    pipelineTrace.info("client_environment", {
      userAgent: navigator.userAgent,
      ...(userAgentData
        ? {
            platform: userAgentData.platform,
            mobile: userAgentData.mobile,
            brands: userAgentData.brands
              .map(({ brand, version }) => `${brand} ${version}`)
              .join("; "),
          }
        : {}),
    })
    primeCallSounds()
    dispatch(voiceSessionStartRequested({ chatId, mediaStream, audioProfile }))
  },
})
startAppListening({
  actionCreator: voiceChatActions.voiceModeClosed,
  effect: (action, { dispatch }) => {
    const session = voiceSessionAnalytics.end(action.payload.reason)
    // Ahead of teardown: the pop rides the call's live capture (call-sounds.ts).
    // Every way a call can end reaches here.
    const endSound = playEndCallSound()
    // The stop listener runs synchronously within this dispatch, so teardown
    // traces are recorded before the context closes.
    dispatch(voiceSessionStopRequested({ holdCaptureUntil: endSound }))
    if (session?.chatId != null) {
      void uploadLightVoiceTrace(
        dispatch,
        session.chatId,
        session.voiceSessionId,
        "closed"
      )
    }
    stopVoiceTraceContext()
  },
})

// Navigating away from the origin chat ends the call. A live session is bound
// to the chat it started on, so once the viewed chat changes to a different id
// (chat→chat) or clears (chat→non-chat: chat.id → 0), the session is torn down
// rather than kept running headless. `setChat`/`resetChat` are the only actions
// that change `chat.id` (see user-slice), and listener effects run after the
// reducer, so `selectChatId` here is the post-navigation id. Opening a same-chat
// sheet does NOT touch `chat.id`, so this never fires on a sheet open — that
// case stays live under the mini-player (see selectVoiceOverlayCoveredBySheet).
startAppListening({
  matcher: isAnyOf(userSlice.actions.setChat, userSlice.actions.resetChat),
  effect: (_action, { dispatch, getState }) => {
    const { voiceModeOpen, voiceModeChatId } = selectVoiceChatState(getState())
    if (
      voiceModeOpen &&
      voiceModeChatId !== null &&
      selectChatId(getState()) !== voiceModeChatId
    ) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "left_chat" }))
    }
  },
})

// Navigating to a different page ends the call the same way. A call opened on
// a NON-chat page (a trip, the app home) gets no setChat/resetChat when the
// user navigates on, so the listener above can never fire for it —
// `setPreviousPage` covers that gap: the route handler dispatches it at the
// start of every navigation whose pathname changes, and never for same-path
// query changes (the entity sheets' ?ref=), so a sheet covering the overlay
// stays the mini-player flow while any true departure hangs the call up
// instead of letting the overlay follow the user. A call the user spoke in is
// marked as the Chat tab's resume target on the way out, like a hang-up.
startAppListening({
  actionCreator: setPreviousPage,
  effect: (_action, { dispatch, getState }) => {
    const state = getState()
    const { voiceModeOpen, voiceModeChatId } = selectVoiceChatState(state)
    if (voiceModeOpen && voiceModeChatId !== null) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "left_chat" }))
      markCompletedVoiceChat(dispatch, state)
    }
  },
})

// Surface each persisted voice turn in the main chat thread live, not only
// on exit.
startAppListening({
  actionCreator: voiceChatActions.turnPersisted,
  effect: (_action, { dispatch, getState }) => {
    const { selectedChatId } = selectVoiceChatState(getState())
    syncVoiceTurnsIntoMainChat(dispatch, getState, selectedChatId)
  },
})

// Assistant barge-in stop
startAppListening({
  actionCreator: voiceAssistantStopRequested,
  effect: () => {
    activeVoiceRtc?.stopAssistant()
  },
})

// Settings — mute (toggle reducer already flipped state)
startAppListening({
  actionCreator: voiceChatActions.mutedToggled,
  effect: (_action, { getState }) => {
    const state = selectVoiceChatState(getState())
    activeVoiceRtc?.applyControls(state)
    transportTrace.info("mute_toggled", { muted: state.muted })
  },
})

// Pause/resume — the assistant stops speaking and resume re-speaks the
// cut-off remainder. The reducer already flipped the flags, including the
// pause's auto-mute (see `mutedByPause`); the engine owns the order they
// land in (VoiceChatRTC.applyControls). A pause the customer ends by
// speaking instead clears through the `pauseAbandoned` transition below.
startAppListening({
  actionCreator: voiceChatActions.pausedToggled,
  effect: (_action, { getState }) => {
    const { paused, muted } = selectVoiceChatState(getState())
    activeVoiceRtc?.applyControls({ paused, muted })
    transportTrace.info("pause_toggled", { paused, muted })
  },
})

// A pause abandoned by customer speech takes its auto-mute with it (the
// reducer restores `muted`); the engine's mic has to follow, or the track
// stays disabled under an unmuted toggle. Idempotent when the customer had
// already unmuted by hand.
startAppListening({
  actionCreator: voiceChatActions.turnTransitionApplied,
  effect: (action, { getState }) => {
    if (action.payload.kind !== "pauseAbandoned") return
    activeVoiceRtc?.setMuted(selectVoiceChatState(getState()).muted)
  },
})

// Settings — output speed
startAppListening({
  actionCreator: voiceChatActions.selectedSpeedChanged,
  effect: ({ payload: speed }) => {
    activeVoiceRtc?.selectOutputSpeed(speed)
    transportTrace.info("voice_output_speed_selected", { speed })
  },
})

// Settings — output device
startAppListening({
  actionCreator: voiceChatActions.selectedOutputDeviceChanged,
  effect: ({ payload: deviceId }) => {
    void activeVoiceRtc?.applySelectedOutputDevice(deviceId)
    settingsTrace.info("output_device_selected", {
      outputDeviceMode:
        deviceId === VOICE_AUDIO_SYSTEM_DEVICE_ID ? "system" : "selected",
    })
  },
})

// Settings trace only; applies to next session.
startAppListening({
  matcher: isAnyOf(voiceChatActions.selectedInputDeviceChanged),
  effect: (action) => {
    settingsTrace.info("settings_changed", {
      setting: "inputDevice",
      value:
        action.payload === VOICE_AUDIO_SYSTEM_DEVICE_ID ? "system" : "selected",
    })
  },
})

// Caption pacer tick — keep the output-speed flush going while revealing
startAppListening({
  actionCreator: voiceChatActions.assistantTranscriptPaced,
  effect: () => {
    activeVoiceRtc?.flushOutputSpeed()
  },
})

// Reconcile running tool calls when the persisted list loads during an active session
startAppListening({
  matcher: voiceChatApi.endpoints.listToolCalls.matchFulfilled,
  effect: ({ payload, meta }, { getState }) => {
    const chatId = meta.arg.originalArgs
    const state = selectVoiceChatState(getState())
    if (state.openaiCallId && chatId === state.selectedChatId) {
      activeVoiceRtc?.reconcileRunningToolCalls(
        chatId,
        state.openaiCallId,
        payload,
        "dto"
      )
    }
  },
})

// Reconcile running tool calls when a session opens on an already-loaded chat
startAppListening({
  actionCreator: voiceChatActions.connected,
  effect: ({ payload: { chatId, openaiCallId } }, { getState }) => {
    const cachedToolCalls =
      voiceChatApi.endpoints.listToolCalls.select(chatId)(getState()).data
    if (cachedToolCalls) {
      activeVoiceRtc?.reconcileRunningToolCalls(
        chatId,
        openaiCallId,
        cachedToolCalls,
        "dto"
      )
    }
  },
})

/**
 * Export for testing - tear down and drop the live session.
 */
export function __resetVoiceChatMiddlewareForTesting() {
  voiceSessionEpoch += 1
  activeVoiceRtc?.close()
  activeVoiceRtc = null
  voiceAudioElement = null
  locationEntityRef = null
  locationRequestSeq = 0
  failedStartTraceUploadCount = 0
  voiceSessionAnalytics.reset()
}
